import React, { useState, useEffect } from "react";
import { openRazorpay } from "../utils/openRazorpay";

export default function PaymentModal({ plan, onClose }) {
    const [selectedDuration, setSelectedDuration] = useState("monthly");
    const [amount, setAmount] = useState(4999);

    // Set amount based on selected duration
    useEffect(() => {
        if (plan?.title === "Standard") {
            setAmount(selectedDuration === "monthly" ? 4999 : 9999);
        }
    }, [selectedDuration, plan]);

    // If plan has custom amount (from PricingCard selection), use that
    useEffect(() => {
        if (plan?.amount) {
            setAmount(plan.amount);
            setSelectedDuration(plan.amount === 4999 ? "monthly" : "quarterly");
        }
    }, [plan]);

    const handlePay = () => {
        const numericAmount = Number(amount);
        if (!numericAmount || numericAmount < 10) {
            alert("Minimum amount is ₹10");
            return;
        }
        openRazorpay({ amount: numericAmount, plan: plan?.title || "Adeya IMS" });
        onClose();
    };

    // Check if Standard plan has predefined options
    const isStandardPlan = plan?.title === "Standard";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop with Blur */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fadeIn" 
                onClick={onClose} 
            />

            {/* Modal Card */}
            <div className="relative bg-white w-full max-w-md rounded-[2rem] p-8 shadow-2xl animate-scaleIn overflow-hidden border border-gray-100">
                
                {/* Decorative Gradient Flare */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-100 rounded-full blur-3xl opacity-50" />
                
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {isStandardPlan ? "Choose Your Plan" : "Start with your budget"}
                </h2>

                <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                    {isStandardPlan 
                        ? `Select your preferred billing cycle for ${plan?.title} plan.` 
                        : `Enter the amount you'd like to invest to get started with Adeya IMS.`
                    }
                </p>

                {/* Standard Plan — Predefined Options */}
                {isStandardPlan ? (
                    <div className="space-y-4 mb-6">
                        <div className="flex gap-4">
                            <button
                                onClick={() => setSelectedDuration("monthly")}
                                className={`flex-1 border-2 rounded-xl p-4 text-center transition-all ${
                                    selectedDuration === "monthly" 
                                        ? "border-blue-600 bg-blue-50" 
                                        : "border-gray-200 hover:border-blue-300"
                                }`}
                            >
                                <div className="font-bold text-lg text-slate-800">₹4,999</div>
                                <div className="text-sm text-slate-500">Monthly</div>
                            </button>
                            <button
                                onClick={() => setSelectedDuration("quarterly")}
                                className={`flex-1 border-2 rounded-xl p-4 text-center transition-all ${
                                    selectedDuration === "quarterly" 
                                        ? "border-blue-600 bg-blue-50" 
                                        : "border-gray-200 hover:border-blue-300"
                                }`}
                            >
                                <div className="font-bold text-lg text-slate-800">₹9,999</div>
                                <div className="text-sm text-slate-500">3 Months</div>
                                <div className="text-xs text-green-600 font-medium">Save ₹4,998</div>
                            </button>
                        </div>
                        <div className="text-center text-xs text-slate-400">
                            Total payable: <span className="font-bold text-slate-700">₹{amount}</span>
                        </div>
                    </div>
                ) : (
                    /* Custom Amount Input for other plans */
                    <div className="relative mb-6 group">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg group-focus-within:text-black transition-colors">
                            ₹
                        </span>
                        <input 
                            type="number"
                            placeholder="Enter amount"
                            className="w-full border-2 border-gray-100 rounded-2xl pl-10 pr-4 py-4 text-xl font-bold focus:border-black focus:outline-none transition-all placeholder:text-gray-200"
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            autoFocus
                        />
                    </div>
                )}

                {/* Primary Action Button */}
                <button
                    onClick={handlePay}
                    className="w-full bg-black text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl active:scale-[0.98] transition-all relative overflow-hidden group"
                >
                    <span className="relative z-10">
                        {isStandardPlan ? `Pay ₹${amount}` : "Proceed to secure payment"}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>

                {/* Cancel Button */}
                <button
                    onClick={onClose}
                    className="w-full mt-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                >
                    Cancel and go back
                </button>

                {/* Security Badge */}
                <div className="mt-6 flex items-center justify-center gap-2 opacity-40">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[10px] uppercase tracking-widest font-bold">Secured by Razorpay</span>
                </div>
            </div>
        </div>
    );
}